import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Star, Edit, Trash2, Plus, X, Check } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminSubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [activeTab, setActiveTab] = useState('en');
  const [formData, setFormData] = useState({
    plan_title_en: '',
    plan_title_ru: '',
    plan_title_kz: '',
    description_en: '',
    description_ru: '',
    description_kz: '',
    price: '',
    is_visible: true,
    is_default: false
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const apiUrl = `${API}/api/admin/subscription-plans`;
      console.log('Fetching plans from:', apiUrl);
      const response = await axios.get(apiUrl);
      console.log('Plans response:', response.data);
      setPlans(response.data || []);
      setError(null);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError(error.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPlan(null);
    setFormData({
      plan_title_en: '',
      plan_title_ru: '',
      plan_title_kz: '',
      description_en: '',
      description_ru: '',
      description_kz: '',
      price: '',
      is_visible: true,
      is_default: false
    });
    setActiveTab('en');
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    const planTitle = plan.plan_title || {};
    const planDesc = plan.description || {};
    setFormData({
      plan_title_en: planTitle.en || '',
      plan_title_ru: planTitle.ru || '',
      plan_title_kz: planTitle.kz || '',
      description_en: planDesc.en || '',
      description_ru: planDesc.ru || '',
      description_kz: planDesc.kz || '',
      price: plan.price.toString(),
      is_visible: plan.is_visible,
      is_default: plan.is_default
    });
    setActiveTab('en');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.plan_title_en || !formData.description_en || !formData.price) {
      alert('Please fill in all required English fields (Title, Description, Price)');
      return;
    }

    if (parseFloat(formData.price) <= 0) {
      alert('Price must be greater than 0');
      return;
    }

    try {
      if (editingPlan) {
        // Update existing plan
        await axios.put(`${API}/api/admin/subscription-plans/${editingPlan.id}`, formData);
      } else {
        // Create new plan
        await axios.post(`${API}/api/admin/subscription-plans`, formData);
      }
      
      setShowModal(false);
      fetchPlans();
    } catch (error) {
      console.error('Error saving plan:', error);
      alert('Error saving plan. Please try again.');
    }
  };

  const handleDelete = async (planId, planName) => {
    if (!window.confirm(`Are you sure you want to delete "${planName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/api/admin/subscription-plans/${planId}`);
      fetchPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert('Error deleting plan. Please try again.');
    }
  };

  const handleToggleVisibility = async (planId) => {
    try {
      await axios.patch(`${API}/api/admin/subscription-plans/${planId}/toggle-visibility`);
      fetchPlans();
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  const handleSetDefault = async (planId) => {
    try {
      await axios.patch(`${API}/api/admin/subscription-plans/${planId}/set-default`);
      fetchPlans();
    } catch (error) {
      console.error('Error setting default:', error);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600">Loading subscription plans...</div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-red-800 mb-2">Error Loading Plans</h2>
            <p className="text-red-600">{error}</p>
            <button 
              onClick={fetchPlans}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-black">Subscription Plans</h1>
            <p className="text-gray-600 mt-1">Manage gift card subscription plans and pricing</p>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 bg-[#1E88E5] text-white px-4 py-2 rounded-lg hover:bg-[#1976D2] transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add New Plan
          </button>
        </div>

        {/* Plans Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Plan Title</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Price</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Translations</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Visible</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Default</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    No plans found. Create your first plan to get started.
                  </td>
                </tr>
              ) : (
                plans.map((plan) => {
                  // Safety checks for nested properties
                  const planTitle = plan.plan_title || {};
                  const planDesc = plan.description || {};
                  const hasRussian = planTitle.ru && planTitle.ru.length > 0;
                  const hasKazakh = planTitle.kz && planTitle.kz.length > 0;
                  
                  return (
                    <tr key={plan.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-semibold text-black">{planTitle.en || 'Untitled'}</div>
                        <div className="text-sm text-gray-600">{planDesc.en || ''}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-lg font-bold text-[#1E88E5]">${plan.price.toFixed(2)}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex justify-center gap-2">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">EN</span>
                          {hasRussian ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded flex items-center gap-1">
                              <Check className="w-3 h-3" /> RU
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">RU</span>
                          )}
                          {hasKazakh ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded flex items-center gap-1">
                              <Check className="w-3 h-3" /> KZ
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">KZ</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => handleToggleVisibility(plan.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            plan.is_visible
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {plan.is_visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => handleSetDefault(plan.id)}
                          disabled={plan.is_default}
                          className={`p-2 rounded-lg transition-colors ${
                            plan.is_default
                              ? 'bg-yellow-100 text-yellow-700 cursor-default'
                              : 'bg-gray-100 text-gray-500 hover:bg-yellow-100 hover:text-yellow-700'
                          }`}
                        >
                          <Star className={`w-5 h-5 ${plan.is_default ? 'fill-yellow-700' : ''}`} />
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEdit(plan)}
                            className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(plan.id, planTitle.en || 'this plan')}
                            className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-black">
                  {editingPlan ? 'Edit Plan' : 'Create New Plan'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                {/* Language Tabs */}
                <div className="flex gap-2 mb-6 border-b">
                  <button
                    type="button"
                    onClick={() => setActiveTab('en')}
                    className={`px-4 py-2 font-semibold transition-colors ${
                      activeTab === 'en'
                        ? 'text-[#1E88E5] border-b-2 border-[#1E88E5]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    English (Master) *
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('ru')}
                    className={`px-4 py-2 font-semibold transition-colors ${
                      activeTab === 'ru'
                        ? 'text-[#1E88E5] border-b-2 border-[#1E88E5]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Russian (RU)
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('kz')}
                    className={`px-4 py-2 font-semibold transition-colors ${
                      activeTab === 'kz'
                        ? 'text-[#1E88E5] border-b-2 border-[#1E88E5]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Kazakh (KZ)
                  </button>
                </div>

                {/* English Tab */}
                {activeTab === 'en' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Plan Title (English) *
                      </label>
                      <input
                        type="text"
                        value={formData.plan_title_en}
                        onChange={(e) => setFormData({...formData, plan_title_en: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                        placeholder="e.g., Individual Annual Plan"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Description (English) *
                      </label>
                      <textarea
                        value={formData.description_en}
                        onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                        rows="3"
                        placeholder="e.g., 12 months of MethodMate Individual access"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Price (USD) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                        placeholder="99.00"
                        required
                      />
                    </div>

                    <div className="flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_visible}
                          onChange={(e) => setFormData({...formData, is_visible: e.target.checked})}
                          className="w-5 h-5 text-[#1E88E5] rounded focus:ring-2 focus:ring-[#1E88E5]"
                        />
                        <span className="text-sm font-medium text-gray-700">Display on card customization page</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_default}
                          onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
                          className="w-5 h-5 text-[#1E88E5] rounded focus:ring-2 focus:ring-[#1E88E5]"
                        />
                        <span className="text-sm font-medium text-gray-700">Pre-select by default</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Russian Tab */}
                {activeTab === 'ru' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Plan Title (Russian)
                      </label>
                      <input
                        type="text"
                        value={formData.plan_title_ru}
                        onChange={(e) => setFormData({...formData, plan_title_ru: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                        placeholder="Индивидуальный годовой план"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Description (Russian)
                      </label>
                      <textarea
                        value={formData.description_ru}
                        onChange={(e) => setFormData({...formData, description_ru: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                        rows="3"
                        placeholder="12 месяцев доступа к платформе MethodMate"
                      />
                    </div>
                  </div>
                )}

                {/* Kazakh Tab */}
                {activeTab === 'kz' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Plan Title (Kazakh)
                      </label>
                      <input
                        type="text"
                        value={formData.plan_title_kz}
                        onChange={(e) => setFormData({...formData, plan_title_kz: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                        placeholder="Жеке жылдық жоспар"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Description (Kazakh)
                      </label>
                      <textarea
                        value={formData.description_kz}
                        onChange={(e) => setFormData({...formData, description_kz: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                        rows="3"
                        placeholder="12 айға MethodMate платформасына жазылым"
                      />
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-[#1E88E5] text-white rounded-lg hover:bg-[#1976D2] transition-colors"
                  >
                    {editingPlan ? 'Update Plan' : 'Create Plan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSubscriptionPlans;
